/**
 * Color utility functions for the ColorPicker module
 * Handles color format conversions, parsing, and gradient utilities
 */

// ============================================================================
// Default Values
// ============================================================================

/**
 * Default solid color value
 * @type {{type: 'solid', color: string, alpha: number}}
 */
export const DEFAULT_COLOR = {
  type: 'solid',
  color: '#000000',
  alpha: 100,
};

/**
 * Default linear gradient value
 * @type {{type: 'linear-gradient', angle: number, stops: Array}}
 */
export const DEFAULT_GRADIENT = {
  type: 'linear-gradient',
  angle: 90,
  stops: [
    { color: '#000000', alpha: 100, position: 0 },
    { color: '#ffffff', alpha: 100, position: 100 },
  ],
};

/**
 * Preset colors for quick selection
 * Organized by color families: reds, oranges, yellows, greens, blues, purples, grays, black, white
 * @type {string[]}
 */
export const PRESET_COLORS = [
  // Reds
  '#FF0000',
  '#DC2626',
  '#B91C1C',
  '#991B1B',
  '#7F1D1D',
  // Oranges
  '#FF6600',
  '#EA580C',
  '#C2410C',
  '#9A3412',
  '#7C2D12',
  // Yellows
  '#FFCC00',
  '#EAB308',
  '#CA8A04',
  '#A16207',
  '#854D0E',
  // Greens
  '#00FF00',
  '#16A34A',
  '#15803D',
  '#166534',
  '#14532D',
  // Blues
  '#0066FF',
  '#2563EB',
  '#1D4ED8',
  '#1E40AF',
  '#1E3A8A',
  // Purples
  '#9900FF',
  '#9333EA',
  '#7E22CE',
  '#6B21A8',
  '#581C87',
  // Grays
  '#F5F5F5',
  '#D4D4D4',
  '#A3A3A3',
  '#737373',
  '#525252',
  // Black and White
  '#FFFFFF',
  '#000000',
];

// ============================================================================
// Color Format Conversions
// ============================================================================

/**
 * Convert hex color string to RGB object
 * @param {string} hex - Hex color string (with or without #)
 * @returns {{r: number, g: number, b: number}|null} RGB object or null if invalid
 */
export function hexToRgb(hex) {
  if (!hex || typeof hex !== 'string') {
    return null;
  }

  // Remove # if present
  const cleanHex = hex.replace(/^#/, '');

  // Handle 3-character hex
  let fullHex = cleanHex;
  if (cleanHex.length === 3) {
    fullHex = cleanHex
      .split('')
      .map((char) => char + char)
      .join('');
  }

  // Validate hex length
  if (fullHex.length !== 6) {
    return null;
  }

  // Parse hex values
  const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex);
  if (!result) {
    return null;
  }

  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  };
}

/**
 * Convert RGB values to hex string
 * @param {number} r - Red value (0-255)
 * @param {number} g - Green value (0-255)
 * @param {number} b - Blue value (0-255)
 * @returns {string} Hex color string with # prefix
 */
export function rgbToHex(r, g, b) {
  // Clamp values to valid range
  const clamp = (val) => Math.max(0, Math.min(255, Math.round(val)));

  const rHex = clamp(r).toString(16).padStart(2, '0');
  const gHex = clamp(g).toString(16).padStart(2, '0');
  const bHex = clamp(b).toString(16).padStart(2, '0');

  return `#${rHex}${gHex}${bHex}`.toUpperCase();
}

/**
 * Convert RGB to HSL
 * @param {number} r - Red value (0-255)
 * @param {number} g - Green value (0-255)
 * @param {number} b - Blue value (0-255)
 * @returns {{h: number, s: number, l: number}} HSL object (h: 0-360, s: 0-100, l: 0-100)
 */
export function rgbToHsl(r, g, b) {
  // Normalize RGB values to 0-1 range
  const rNorm = r / 255;
  const gNorm = g / 255;
  const bNorm = b / 255;

  const max = Math.max(rNorm, gNorm, bNorm);
  const min = Math.min(rNorm, gNorm, bNorm);
  const delta = max - min;

  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (delta !== 0) {
    // Calculate saturation
    s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min);

    // Calculate hue
    switch (max) {
      case rNorm:
        h = ((gNorm - bNorm) / delta + (gNorm < bNorm ? 6 : 0)) / 6;
        break;
      case gNorm:
        h = ((bNorm - rNorm) / delta + 2) / 6;
        break;
      case bNorm:
        h = ((rNorm - gNorm) / delta + 4) / 6;
        break;
      default:
        // max must be one of rNorm, gNorm, or bNorm
        break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

/**
 * Convert HSL to RGB
 * @param {number} h - Hue (0-360)
 * @param {number} s - Saturation (0-100)
 * @param {number} l - Lightness (0-100)
 * @returns {{r: number, g: number, b: number}} RGB object (0-255)
 */
export function hslToRgb(h, s, l) {
  // Normalize values
  const hNorm = ((h % 360) + 360) % 360 / 360;
  const sNorm = Math.max(0, Math.min(100, s)) / 100;
  const lNorm = Math.max(0, Math.min(100, l)) / 100;

  let r, g, b;

  if (sNorm === 0) {
    // Achromatic (gray)
    r = g = b = lNorm;
  } else {
    const hueToRgb = (p, q, t) => {
      let tNorm = t;
      if (tNorm < 0) tNorm += 1;
      if (tNorm > 1) tNorm -= 1;
      if (tNorm < 1 / 6) return p + (q - p) * 6 * tNorm;
      if (tNorm < 1 / 2) return q;
      if (tNorm < 2 / 3) return p + (q - p) * (2 / 3 - tNorm) * 6;
      return p;
    };

    const q = lNorm < 0.5 ? lNorm * (1 + sNorm) : lNorm + sNorm - lNorm * sNorm;
    const p = 2 * lNorm - q;

    r = hueToRgb(p, q, hNorm + 1 / 3);
    g = hueToRgb(p, q, hNorm);
    b = hueToRgb(p, q, hNorm - 1 / 3);
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
  };
}

/**
 * Convert hex color to HSL
 * @param {string} hex - Hex color string
 * @returns {{h: number, s: number, l: number}|null} HSL object or null if invalid
 */
export function hexToHsl(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) {
    return null;
  }
  return rgbToHsl(rgb.r, rgb.g, rgb.b);
}

/**
 * Convert HSL to hex color
 * @param {number} h - Hue (0-360)
 * @param {number} s - Saturation (0-100)
 * @param {number} l - Lightness (0-100)
 * @returns {string} Hex color string with # prefix
 */
export function hslToHex(h, s, l) {
  const rgb = hslToRgb(h, s, l);
  return rgbToHex(rgb.r, rgb.g, rgb.b);
}

// ============================================================================
// Color Value Parsing/Formatting
// ============================================================================

/**
 * Validate hex color string
 * @param {string} hex - Hex color string to validate
 * @returns {boolean} True if valid hex color
 */
export function isValidHex(hex) {
  if (!hex || typeof hex !== 'string') {
    return false;
  }
  const cleanHex = hex.replace(/^#/, '');
  return /^[0-9A-Fa-f]{3}$|^[0-9A-Fa-f]{6}$/.test(cleanHex);
}

/**
 * Normalize hex color to 6-character format with # prefix
 * @param {string} hex - Hex color string
 * @returns {string} Normalized hex string (e.g., '#FFFFFF')
 */
export function normalizeHex(hex) {
  if (!hex || typeof hex !== 'string') {
    return '#000000';
  }

  // Remove # if present
  let cleanHex = hex.replace(/^#/, '');

  // Expand 3-character hex to 6-character
  if (cleanHex.length === 3) {
    cleanHex = cleanHex
      .split('')
      .map((char) => char + char)
      .join('');
  }

  // Validate and return
  if (/^[0-9A-Fa-f]{6}$/.test(cleanHex)) {
    return `#${cleanHex.toUpperCase()}`;
  }

  return '#000000';
}

/**
 * Parse any color value into normalized object format
 * @param {string|object} value - Color value (hex string, 'transparent', or object format)
 * @returns {{type: string, color?: string, alpha?: number, angle?: number, shape?: string, position?: string, stops?: Array}}
 */
export function parseColorValue(value) {
  // Handle null/undefined
  if (value === null || value === undefined) {
    return { ...DEFAULT_COLOR };
  }

  // Handle transparent
  if (value === 'transparent') {
    return { type: 'solid', color: '#000000', alpha: 0 };
  }

  // Handle string hex values
  if (typeof value === 'string') {
    if (isValidHex(value)) {
      return {
        type: 'solid',
        color: normalizeHex(value),
        alpha: 100,
      };
    }
    // Try to parse CSS gradient strings
    if (value.includes('linear-gradient')) {
      return parseLinearGradientString(value);
    }
    if (value.includes('radial-gradient')) {
      return parseRadialGradientString(value);
    }
    // Invalid string, return default
    return { ...DEFAULT_COLOR };
  }

  // Handle object format
  if (typeof value === 'object') {
    const { type } = value;

    if (type === 'solid') {
      return {
        type: 'solid',
        color: normalizeHex(value.color || '#000000'),
        alpha: typeof value.alpha === 'number' ? Math.max(0, Math.min(100, value.alpha)) : 100,
      };
    }

    if (type === 'linear-gradient') {
      return {
        type: 'linear-gradient',
        angle: typeof value.angle === 'number' ? value.angle : 90,
        stops: Array.isArray(value.stops) ? sortGradientStops(value.stops) : DEFAULT_GRADIENT.stops,
      };
    }

    if (type === 'radial-gradient') {
      return {
        type: 'radial-gradient',
        shape: value.shape || 'ellipse',
        position: value.position || 'center',
        stops: Array.isArray(value.stops) ? sortGradientStops(value.stops) : DEFAULT_GRADIENT.stops,
      };
    }
  }

  // Fallback to default
  return { ...DEFAULT_COLOR };
}

/**
 * Parse a CSS linear-gradient string into object format
 * @param {string} gradientStr - CSS linear-gradient string
 * @returns {object} Parsed gradient object
 */
function parseLinearGradientString(gradientStr) {
  const result = {
    type: 'linear-gradient',
    angle: 90,
    stops: [],
  };

  try {
    // Extract content between parentheses
    const match = gradientStr.match(/linear-gradient\(([^)]+)\)/i);
    if (!match) return { ...DEFAULT_GRADIENT };

    const content = match[1];
    const parts = content.split(',').map((p) => p.trim());

    // Check if first part is angle
    const angleMatch = parts[0].match(/^(\d+)deg$/);
    if (angleMatch) {
      result.angle = parseInt(angleMatch[1], 10);
      parts.shift();
    }

    // Parse color stops
    parts.forEach((part) => {
      const stopMatch = part.match(/(#[0-9A-Fa-f]{3,6}|rgba?\([^)]+\))\s*(\d+%)?/);
      if (stopMatch) {
        const color = stopMatch[1].startsWith('#') ? normalizeHex(stopMatch[1]) : '#000000';
        const position = stopMatch[2] ? parseInt(stopMatch[2], 10) : 0;
        result.stops.push({ color, alpha: 100, position });
      }
    });

    if (result.stops.length < 2) {
      return { ...DEFAULT_GRADIENT };
    }

    return result;
  } catch {
    return { ...DEFAULT_GRADIENT };
  }
}

/**
 * Parse a CSS radial-gradient string into object format
 * @param {string} gradientStr - CSS radial-gradient string
 * @returns {object} Parsed gradient object
 */
function parseRadialGradientString(gradientStr) {
  const result = {
    type: 'radial-gradient',
    shape: 'ellipse',
    position: 'center',
    stops: [],
  };

  try {
    // Extract content between parentheses
    const match = gradientStr.match(/radial-gradient\(([^)]+)\)/i);
    if (!match) {
      return { ...result, stops: DEFAULT_GRADIENT.stops };
    }

    const content = match[1];
    const parts = content.split(',').map((p) => p.trim());

    // Check if first part defines shape/position
    if (parts[0] && !parts[0].startsWith('#') && !parts[0].startsWith('rgb')) {
      const shapeMatch = parts[0].match(/(circle|ellipse)/i);
      if (shapeMatch) {
        result.shape = shapeMatch[1].toLowerCase();
      }
      const posMatch = parts[0].match(/at\s+(.+)/i);
      if (posMatch) {
        result.position = posMatch[1];
      }
      parts.shift();
    }

    // Parse color stops
    parts.forEach((part) => {
      const stopMatch = part.match(/(#[0-9A-Fa-f]{3,6}|rgba?\([^)]+\))\s*(\d+%)?/);
      if (stopMatch) {
        const color = stopMatch[1].startsWith('#') ? normalizeHex(stopMatch[1]) : '#000000';
        const position = stopMatch[2] ? parseInt(stopMatch[2], 10) : 0;
        result.stops.push({ color, alpha: 100, position });
      }
    });

    if (result.stops.length < 2) {
      result.stops = [...DEFAULT_GRADIENT.stops];
    }

    return result;
  } catch {
    return { ...result, stops: DEFAULT_GRADIENT.stops };
  }
}

/**
 * Convert color value (string or object) to valid CSS string
 * Handles both legacy string formats and new object formats for backwards compatibility
 * @param {string|object} value - Color value (hex string, 'transparent', or color object)
 * @param {string} [fallback] - Optional fallback value if color is invalid/null
 * @returns {string|null} CSS color string, or null if value is falsy and no fallback provided
 */
export function colorValueToCss(value, fallback) {
  // Handle null/undefined - return fallback or null
  if (value === null || value === undefined || value === '') {
    return fallback !== undefined ? fallback : null;
  }

  // Handle 'transparent' string
  if (value === 'transparent') {
    return 'transparent';
  }

  // Handle string format (legacy/backwards compatible)
  if (typeof value === 'string') {
    // Valid hex color
    if (isValidHex(value)) {
      return normalizeHex(value);
    }
    // CSS gradient strings pass through
    if (value.includes('gradient')) {
      return value;
    }
    // Invalid string - return fallback or the string as-is (might be a CSS color name)
    return fallback !== undefined ? fallback : value;
  }

  // Handle object format
  if (typeof value === 'object') {
    const { type } = value;

    if (type === 'solid') {
      const { color, alpha } = value;
      const normalizedColor = normalizeHex(color || '#000000');
      const normalizedAlpha = typeof alpha === 'number' ? alpha : 100;

      if (normalizedAlpha === 0) {
        return 'transparent';
      }

      if (normalizedAlpha === 100) {
        return normalizedColor;
      }

      // Convert to rgba
      const rgb = hexToRgb(normalizedColor);
      if (rgb) {
        return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${normalizedAlpha / 100})`;
      }

      return normalizedColor;
    }

    if (type === 'linear-gradient' || type === 'radial-gradient') {
      return generateGradientCss(value);
    }
  }

  // Fallback
  return fallback !== undefined ? fallback : null;
}

/**
 * Extract solid color from color value (ignores gradients)
 * Useful for properties that don't support gradients (borderColor, text color, etc.)
 * @param {string|object} value - Color value
 * @param {string} [fallback] - Optional fallback color
 * @returns {string|null} Solid CSS color string, or null/fallback if gradient or invalid
 */
export function colorValueToSolidCss(value, fallback) {
  // Handle null/undefined
  if (value === null || value === undefined || value === '') {
    return fallback !== undefined ? fallback : null;
  }

  // Handle 'transparent' string
  if (value === 'transparent') {
    return 'transparent';
  }

  // Handle string format
  if (typeof value === 'string') {
    if (isValidHex(value)) {
      return normalizeHex(value);
    }
    // For gradients or invalid strings, return fallback
    if (value.includes('gradient')) {
      return fallback !== undefined ? fallback : null;
    }
    return fallback !== undefined ? fallback : value;
  }

  // Handle object format
  if (typeof value === 'object') {
    const { type } = value;

    // Only process solid colors
    if (type === 'solid') {
      const { color, alpha } = value;
      const normalizedColor = normalizeHex(color || '#000000');
      const normalizedAlpha = typeof alpha === 'number' ? alpha : 100;

      if (normalizedAlpha === 0) {
        return 'transparent';
      }

      if (normalizedAlpha === 100) {
        return normalizedColor;
      }

      const rgb = hexToRgb(normalizedColor);
      if (rgb) {
        return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${normalizedAlpha / 100})`;
      }

      return normalizedColor;
    }

    // For gradients, return fallback (gradients not supported for this use case)
    if (type === 'linear-gradient' || type === 'radial-gradient') {
      return fallback !== undefined ? fallback : null;
    }
  }

  return fallback !== undefined ? fallback : null;
}

// ============================================================================
// Gradient Utilities
// ============================================================================

/**
 * Create a gradient stop object
 * @param {string} color - Hex color string
 * @param {number} alpha - Alpha value (0-100)
 * @param {number} position - Position in gradient (0-100)
 * @returns {{color: string, alpha: number, position: number}} Gradient stop object
 */
export function createGradientStop(color, alpha = 100, position = 0) {
  return {
    color: normalizeHex(color || '#000000'),
    alpha: Math.max(0, Math.min(100, typeof alpha === 'number' ? alpha : 100)),
    position: Math.max(0, Math.min(100, typeof position === 'number' ? position : 0)),
  };
}

/**
 * Sort gradient stops by position
 * @param {Array} stops - Array of gradient stop objects
 * @returns {Array} Sorted array of gradient stops
 */
export function sortGradientStops(stops) {
  if (!Array.isArray(stops)) {
    return [];
  }

  return [...stops]
    .map((stop) => ({
      color: normalizeHex(stop.color || '#000000'),
      alpha: typeof stop.alpha === 'number' ? Math.max(0, Math.min(100, stop.alpha)) : 100,
      position: typeof stop.position === 'number' ? Math.max(0, Math.min(100, stop.position)) : 0,
    }))
    .sort((a, b) => a.position - b.position);
}

/**
 * Generate CSS gradient string from gradient value object
 * @param {object} gradientValue - Gradient value object
 * @returns {string} CSS gradient string
 */
export function generateGradientCss(gradientValue) {
  if (!gradientValue || typeof gradientValue !== 'object') {
    return 'linear-gradient(90deg, #000000 0%, #FFFFFF 100%)';
  }

  const { type, stops } = gradientValue;

  if (!Array.isArray(stops) || stops.length < 2) {
    return 'linear-gradient(90deg, #000000 0%, #FFFFFF 100%)';
  }

  // Generate color stop strings
  const colorStops = sortGradientStops(stops)
    .map((stop) => {
      const { color, alpha, position } = stop;
      const rgb = hexToRgb(color);

      if (!rgb) {
        return `#000000 ${position}%`;
      }

      if (alpha === 100) {
        return `${color} ${position}%`;
      }

      return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha / 100}) ${position}%`;
    })
    .join(', ');

  if (type === 'linear-gradient') {
    const angle = typeof gradientValue.angle === 'number' ? gradientValue.angle : 90;
    return `linear-gradient(${angle}deg, ${colorStops})`;
  }

  if (type === 'radial-gradient') {
    const shape = gradientValue.shape || 'ellipse';
    const position = gradientValue.position || 'center';
    return `radial-gradient(${shape} at ${position}, ${colorStops})`;
  }

  return `linear-gradient(90deg, ${colorStops})`;
}
